import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import { MapContainer, Marker, useMap, useMapEvents } from 'react-leaflet';

import { Button } from './Button';
import { Input } from './Input';
import { OpenFreeMapLayer } from './OpenFreeMapLayer';
import { geocodeAddress, type GeocodeAddress, type GeocodeResult } from '../../utils/geocoding';

type LatLng = {
  latitude: number;
  longitude: number;
};

const defaultCenter: LatLng = { latitude: 51.1657, longitude: 10.4515 };

// Fix default marker icons for Vite builds
const DefaultIcon = L.icon({
  iconUrl: new URL('leaflet/dist/images/marker-icon.png', import.meta.url).toString(),
  iconRetinaUrl: new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).toString(),
  shadowUrl: new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).toString(),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface MapPickerProps {
  value?: LatLng | null;
  onChange: (value: LatLng | null) => void;
  onAddressResolved?: (address: GeocodeAddress) => void;
  className?: string;
  heightClassName?: string;
  disabled?: boolean;
  containerClassName?: string;
  searchQuery?: string;
  showSearch?: boolean;
}

function clampLatLng(value: LatLng): LatLng {
  return {
    latitude: Math.min(90, Math.max(-90, value.latitude)),
    longitude: Math.min(180, Math.max(-180, value.longitude)),
  };
}

function ClickHandler({ onPick }: { onPick: (value: LatLng) => void }) {
  useMapEvents({
    click(e) {
      onPick({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    },
  });

  return null;
}

function FitToValue({
  value,
  zoom,
}: {
  value: LatLng | null;
  zoom: number;
}) {
  const map = useMap();

  useEffect(() => {
    if (!value) return;
    map.setView([value.latitude, value.longitude], zoom, { animate: true });
  }, [map, value, zoom]);

  return null;
}

export default function MapPicker({
  value,
  onChange,
  onAddressResolved,
  className,
  heightClassName = 'h-64',
  disabled,
  containerClassName,
  searchQuery,
  showSearch,
}: MapPickerProps) {
  const { t } = useTranslation(['common']);
  const [isLocating, setIsLocating] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const suppressNextSearchRef = useRef(false);

  const effectiveSearchText = (searchQuery ?? searchText).trim();
  const isControlledSearch = searchQuery !== undefined;

  useEffect(() => {
    if (searchQuery === undefined) return;
    setSearchText(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (!showSearch || disabled) {
      setResults([]);
      setSearchError(null);
      return;
    }

    if (!effectiveSearchText) {
      setResults([]);
      setSearchError(null);
      return;
    }

    if (suppressNextSearchRef.current) {
      suppressNextSearchRef.current = false;
      return;
    }

    const handle = window.setTimeout(async () => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      setIsSearching(true);
      setSearchError(null);
      try {
        const res = await geocodeAddress(effectiveSearchText, ac.signal);
        setResults(res);
      } catch (e) {
        if ((e as { name?: string }).name === 'AbortError') return;
        setResults([]);
        setSearchError(t('common:map.search.error'));
      } finally {
        setIsSearching(false);
      }
    }, 450);

    return () => {
      window.clearTimeout(handle);
      abortRef.current?.abort();
    };
  }, [effectiveSearchText, showSearch, disabled]);

  const center = useMemo(() => {
    if (value?.latitude != null && value?.longitude != null) {
      return [value.latitude, value.longitude] as [number, number];
    }
    return [defaultCenter.latitude, defaultCenter.longitude] as [number, number];
  }, [value]);

  const markerValue = useMemo(() => {
    if (value?.latitude == null || value?.longitude == null) return null;
    return clampLatLng(value);
  }, [value]);

  const marker = useMemo(() => {
    if (!markerValue) return null;
    return [markerValue.latitude, markerValue.longitude] as [number, number];
  }, [markerValue]);

  const mapZoom = marker ? 15 : 6;

  useEffect(() => {
    setIsLocating(false);
  }, [disabled]);

  return (
    <div className={className}>
      {!disabled && (
        <div className="flex flex-col gap-3 mb-3">
          {showSearch && (
            <div className="relative space-y-2">
              <div className="relative">
                <Input
                  value={isControlledSearch ? searchQuery ?? '' : searchText}
                  onChange={(e) => {
                    if (isControlledSearch) return;
                    setSearchText(e.target.value);
                  }}
                  placeholder={t('common:map.search.placeholder')}
                  className="pr-10"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-500">
                  {isSearching ? t('common:map.search.loading') : null}
                </div>
              </div>

              {searchError && <div className="text-[11px] text-red-600">{searchError}</div>}

              {results.length > 0 && (
                <div
                  className="absolute left-0 right-0 top-full mt-1 max-h-44 overflow-auto rounded-md border border-slate-200 bg-white z-50"
                  style={{ boxShadow: '0 10px 30px -10px rgba(15,23,42,0.15)' }}
                >
                  {results.map((r) => (
                    <button
                      key={`${r.latitude}-${r.longitude}-${r.label}`}
                      type="button"
                      onClick={() => {
                        suppressNextSearchRef.current = true;
                        onChange({ latitude: r.latitude, longitude: r.longitude });
                        onAddressResolved?.(r.address);
                        setResults([]);
                      }}
                      className="w-full text-left px-3 py-2 text-[13px] text-slate-900 hover:bg-slate-50 transition-colors"
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled || isLocating || !('geolocation' in navigator)}
              onClick={() => {
                if (disabled) return;
                if (!('geolocation' in navigator)) return;

                setIsLocating(true);
                navigator.geolocation.getCurrentPosition(
                  (pos) => {
                    const picked = clampLatLng({
                      latitude: pos.coords.latitude,
                      longitude: pos.coords.longitude,
                    });
                    onChange(picked);
                    setIsLocating(false);
                  },
                  () => {
                    setIsLocating(false);
                  },
                  { enableHighAccuracy: true, timeout: 8000 }
                );
              }}
            >
              {isLocating ? t('common:map.actions.useMyLocationLoading') : t('common:map.actions.useMyLocation')}
            </Button>

            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled || (!value?.latitude && !value?.longitude)}
              onClick={() => onChange(null)}
            >
              {t('common:map.actions.removeGeolocation')}
            </Button>

            <div className="text-[11px] text-slate-500">{t('common:map.hint')}</div>
          </div>
        </div>
      )}

      <div
        className={`relative z-0 isolate w-full rounded-xl overflow-hidden border border-slate-200 ${heightClassName} ${containerClassName || ''}`}
      >
        <MapContainer
          center={center}
          zoom={mapZoom}
          scrollWheelZoom
          style={{ height: '100%', width: '100%' }}
        >
          <FitToValue value={markerValue} zoom={mapZoom} />

          {!disabled && (
            <ClickHandler
              onPick={(picked) => {
                onChange(clampLatLng(picked));
              }}
            />
          )}

          <OpenFreeMapLayer />

          {marker && (
            <Marker
              position={marker}
              draggable={!disabled}
              eventHandlers={{
                dragend: (evt: L.LeafletEvent) => {
                  const latlng = (evt.target as L.Marker).getLatLng();
                  onChange(clampLatLng({ latitude: latlng.lat, longitude: latlng.lng }));
                },
              }}
            />
          )}
        </MapContainer>
      </div>
    </div>
  );
}
